import { Grid, InputLabel, TextField as MuiTextField } from '@mui/material'
import type { TextFieldProps } from '@mui/material'
import { TimePicker } from '@mui/lab'
import { format } from 'date-fns'
import type { TFunction } from 'i18next'
import { Controller, FieldValues } from 'react-hook-form'
import type { Control } from 'react-hook-form'
import { ScheduleSetTimeCaption } from '../ScheduleSetTimeCaption'
import { DateFieldInputRow } from '../ScheduleFields.style'
import { fieldCaptionLabelSx } from '../ScheduleFields.utils'
import { round } from 'shared/utils'
import {
  createScheduleShouldDisableTime,
  formatScheduleTimeInputDisplay,
  getScheduleTimeFieldLabel,
  SCHEDULE_ACCESS_TIME_MINUTES_STEP,
  scheduleTimeInputPlaceholder,
  type ScheduleFieldNames,
  type ScheduleTranslations,
  type ScheduleValueConfig,
} from '../ScheduleFields.utils'
import {
  scheduleTimePickerDialogProps,
  scheduleTimePickerPopperProps,
} from './ScheduleTimePickers.style'

export type ScheduleTimePickersProps<T extends FieldValues> = {
  control: Control<T>
  fieldNames: ScheduleFieldNames
  disabled: boolean
  shouldShow: boolean
  scheduleAllDay: boolean
  fromTimeOpen: boolean
  toTimeOpen: boolean
  onFromTimeOpen: (open: boolean) => void
  onToTimeOpen: (open: boolean) => void
  firstSelectedDay: string
  lastSelectedDay: string
  frequency: string
  valueConfig: ScheduleValueConfig
  t: TFunction
  labels: ScheduleTranslations
  formatLabel: (text: string) => string
  getFieldError: (fieldKey: string) => string | undefined
  showTimezoneInTimeInputs?: boolean
  scheduleTimeZone?: string
  minutesStep: number
  onTimeChange: (fieldName: string, value: Date | null) => void
  toTimeFooterCaption?: string | null
}

const emptyTimeInputSx = {
  '& .MuiInputBase-input': {
    color: 'text.disabled',
  },
} as const

const roundTimeToMinutesStep = (value: Date | null, minutesStep: number) => {
  if (!value) return null
  const stepMs = minutesStep * 60 * 1000
  return value.getTime() % stepMs === 0 ? value : round(stepMs, value)
}

export function ScheduleTimePickers<T extends FieldValues>({
  control,
  fieldNames,
  disabled,
  shouldShow,
  scheduleAllDay,
  fromTimeOpen,
  toTimeOpen,
  onFromTimeOpen,
  onToTimeOpen,
  firstSelectedDay,
  lastSelectedDay,
  frequency,
  valueConfig,
  t,
  labels,
  formatLabel,
  getFieldError,
  showTimezoneInTimeInputs = false,
  scheduleTimeZone = '',
  minutesStep = SCHEDULE_ACCESS_TIME_MINUTES_STEP,
  onTimeChange,
  toTimeFooterCaption,
}: ScheduleTimePickersProps<T>) {
  if (!shouldShow || scheduleAllDay) return null

  const shouldDisableTime = createScheduleShouldDisableTime(minutesStep)

  const resolveTimeInputProps = (fieldValue: Date | null) => {
    const hasValue = Boolean(fieldValue)

    if (!showTimezoneInTimeInputs) {
      return {
        placeholder: 'hh:mm' as const,
        inputValue: hasValue ? format(fieldValue!, 'HH:mm') : undefined,
        inputValueIsPlaceholder: false,
      }
    }

    const placeholder = scheduleTimeInputPlaceholder(scheduleTimeZone)
    const inputValue = hasValue
      ? formatScheduleTimeInputDisplay(fieldValue, scheduleTimeZone)
      : placeholder

    return {
      placeholder,
      inputValue,
      inputValueIsPlaceholder: !hasValue,
    }
  }

  const renderTimeTextField = (
    params: TextFieldProps,
    options: {
      field: { value: Date | null; onBlur: () => void }
      id: string
      onOpen: () => void
      fieldName: string
    }
  ) => {
    const {
      inputProps: pickerInputProps,
      placeholder: _pickerPlaceholder,
      sx: pickerSx,
      ...dateParams
    } = params
    const { placeholder, inputValue, inputValueIsPlaceholder } =
      resolveTimeInputProps(options.field.value)

    return (
      <MuiTextField
        {...dateParams}
        id={options.id}
        disabled={disabled}
        onBlur={options.field.onBlur}
        onClick={options.onOpen}
        fullWidth
        placeholder={placeholder}
        sx={
          inputValueIsPlaceholder
            ? ([pickerSx, emptyTimeInputSx] as TextFieldProps['sx'])
            : pickerSx
        }
        inputProps={{
          ...pickerInputProps,
          readOnly: true,
          ...(inputValue !== undefined ? { value: inputValue } : {}),
        }}
        error={Boolean(getFieldError(options.fieldName))}
        helperText={getFieldError(options.fieldName)}
      />
    )
  }

  return (
    <>
      <DateFieldInputRow item container>
        <Grid item xs={12} md={9}>
          <InputLabel
            shrink
            htmlFor={`${String(fieldNames.scheduleFromDate)}-time`}
            sx={fieldCaptionLabelSx}
          >
            {formatLabel(
              getScheduleTimeFieldLabel(
                'from',
                firstSelectedDay,
                frequency,
                valueConfig,
                t,
                labels
              )
            )}
          </InputLabel>
          <Controller
            name={fieldNames.scheduleFromDate as any}
            control={control}
            render={({ field }) => (
              <TimePicker
                open={fromTimeOpen}
                onOpen={() => onFromTimeOpen(true)}
                onClose={() => onFromTimeOpen(false)}
                value={field.value}
                ampm={false}
                minutesStep={minutesStep}
                shouldDisableTime={shouldDisableTime}
                DialogProps={scheduleTimePickerDialogProps}
                PopperProps={scheduleTimePickerPopperProps}
                inputFormat="HH:mm"
                onChange={(value: Date | null) =>
                  onTimeChange(
                    fieldNames.scheduleFromDate,
                    roundTimeToMinutesStep(value, minutesStep)
                  )
                }
                renderInput={params =>
                  renderTimeTextField(params, {
                    field: { ...field, value: field.value },
                    id: `${String(fieldNames.scheduleFromDate)}-time`,
                    onOpen: () => onFromTimeOpen(true),
                    fieldName: fieldNames.scheduleFromDate,
                  })
                }
              />
            )}
          />
        </Grid>
      </DateFieldInputRow>
      <DateFieldInputRow container>
        <Grid item xs={12} md={9}>
          <InputLabel
            shrink
            htmlFor={`${String(fieldNames.scheduleToDate)}-time`}
            sx={fieldCaptionLabelSx}
          >
            {formatLabel(
              getScheduleTimeFieldLabel(
                'to',
                lastSelectedDay,
                frequency,
                valueConfig,
                t,
                labels
              )
            )}
          </InputLabel>
          <Controller
            name={fieldNames.scheduleToDate as any}
            control={control}
            render={({ field }) => (
              <TimePicker
                open={toTimeOpen}
                onOpen={() => onToTimeOpen(true)}
                onClose={() => onToTimeOpen(false)}
                value={field.value}
                ampm={false}
                minutesStep={minutesStep}
                shouldDisableTime={shouldDisableTime}
                DialogProps={scheduleTimePickerDialogProps}
                PopperProps={scheduleTimePickerPopperProps}
                inputFormat="HH:mm"
                onChange={(value: Date | null) =>
                  onTimeChange(
                    fieldNames.scheduleToDate,
                    roundTimeToMinutesStep(value, minutesStep)
                  )
                }
                renderInput={params =>
                  renderTimeTextField(params, {
                    field: { ...field, value: field.value },
                    id: `${String(fieldNames.scheduleToDate)}-time`,
                    onOpen: () => onToTimeOpen(true),
                    fieldName: fieldNames.scheduleToDate,
                  })
                }
              />
            )}
          />
          <ScheduleSetTimeCaption caption={toTimeFooterCaption} />
        </Grid>
      </DateFieldInputRow>
    </>
  )
}
